import React from 'react'
import { trackEvent } from 'utils/analytics'

export default function MobileBookingBar({ courses, currentUser, bookingMode, onBooking, onPurchase, onConsult, colors, translations }) {
  if (!courses || courses.length === 0) return null

  const balancesWithLessons = currentUser?.balances?.filter(b => b.remainingLessons > 0) || []
  const hasBalance = balancesWithLessons.length > 0

  const [showManagedNotice, setShowManagedNotice] = React.useState(false)
  const isTeacherManaged = bookingMode === 'teacher_managed'
  const managedNoticeTimerRef = React.useRef(null)

  React.useEffect(() => () => clearTimeout(managedNoticeTimerRef.current), [])

  const handleBookingClick = () => {
    if (isTeacherManaged) {
      clearTimeout(managedNoticeTimerRef.current)
      setShowManagedNotice(true)
      managedNoticeTimerRef.current = setTimeout(() => setShowManagedNotice(false), 3000)
      return
    }
    trackEvent('mobile_bar_click', { action: 'booking' })
    onBooking?.()
  }

  const handlePurchaseClick = () => {
    trackEvent('mobile_bar_click', { action: 'purchase' })
    onPurchase?.()
  }

  // Returning student with balance: top course by remaining lessons + overflow count
  const topBalance = hasBalance
    ? [...balancesWithLessons].sort((a, b) => b.remainingLessons - a.remainingLessons)[0]
    : null
  const otherBalanceCount = hasBalance ? balancesWithLessons.length - 1 : 0

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-white border-t border-gray-200 shadow-[0_-2px_10px_rgba(0,0,0,0.08)]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      {showManagedNotice && (
        <div className="absolute bottom-full left-0 right-0 flex justify-center pb-2 px-4">
          <div className="flex items-center gap-1.5 bg-white rounded-xl shadow-lg border border-gray-200 px-4 py-2.5 text-sm font-medium" style={{ color: colors?.primary || '#3366FF' }}>
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {translations?.teacherWillSchedule || 'Your teacher will schedule lessons for you'}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between px-4 py-3 max-w-lg mx-auto gap-3">

        {/* State A: Returning student with balance — booking flow only */}
        {hasBalance && (
          <>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-600 truncate">{topBalance.courseName}</span>
                <span className="text-xs font-bold whitespace-nowrap" style={{ color: colors?.primary || '#3366FF' }}>
                  {topBalance.remainingLessons} {translations?.lessonsUnit || 'lesson(s)'}
                </span>
              </div>
              {otherBalanceCount > 0 && (
                <span className="text-xs text-gray-400">
                  {(translations?.moreCoursesHint || '+%{count} more').replace('%{count}', otherBalanceCount)}
                </span>
              )}
            </div>
            <button
              onClick={handleBookingClick}
              className="flex-shrink-0 py-3 px-5 rounded-xl font-semibold text-white text-sm shadow-md cursor-pointer whitespace-nowrap"
              style={{ background: colors?.primaryGradient || 'linear-gradient(to right, #3366FF, #5588FF)' }}
            >
              {translations?.bookNow || 'Book Now'}
            </button>
          </>
        )}

        {/* State B: New visitor — purchase button (primary when a consult button
            sits beside it, otherwise the sole outlined CTA) plus an optional
            consult button when consultation is enabled */}
        {!hasBalance && (
          <>
            <button
              onClick={handlePurchaseClick}
              className={`flex-1 py-3 rounded-xl font-semibold text-sm whitespace-nowrap px-5 cursor-pointer ${onConsult ? 'text-white shadow-md' : 'border-2'}`}
              style={onConsult
                ? { background: colors?.primaryGradient || 'linear-gradient(to right, #3366FF, #5588FF)' }
                : { borderColor: colors?.primary || '#3366FF', color: colors?.primary || '#3366FF' }}
            >
              {translations?.purchaseButton || 'Purchase Course'}
            </button>
            {onConsult && (
              <button
                onClick={() => onConsult({ source: 'mobile_bar' })}
                className="flex-1 py-3 rounded-xl font-semibold border-2 cursor-pointer text-sm whitespace-nowrap px-5"
                style={{
                  borderColor: colors?.primary || '#3366FF',
                  color: colors?.primary || '#3366FF'
                }}
              >
                {translations?.freeConsultation || 'Contact a Teacher'}
              </button>
            )}
          </>
        )}

      </div>
    </div>
  )
}
